import { EpLocator } from '../../util/locator';
import { Analytics } from '../../modules/analytics/analytics';
import { autoInjectable, singleton } from 'tsyringe';
import { BaseOverlayComponent } from '../../components/overlays/base-overlay/base-overlay.component';
import { BpOverlayComponentConstructor } from '../../models/component.model';
import { EpLogger } from '../../util/ep-logger';
import { PlacementConfig } from '../../models/placement-config.model';
import {
  getOverlayBaseTemplateByComponentType,
  lookupOverlayConstructor
} from '../../util/constructor-lookup/overlay-constructor-lookup.util';
import {
  GeneratePlacementResponse,
  PlacementContentDef, SSRTextPlacementContent,
} from '@ads-ep/fetch-api-client';
import { getContentById } from '../content-util.service';
import { BpMetricsClickOverlayProperties, BpMetricsViewOverlayProperties } from '@ads-ep/api-metrics';
import { PlacementMetadata } from '../../models/placement-meta-data.model';
import { isSSRContent } from '../../util/content-guards.util';
import { SsrOverlayComponent } from '../../components/overlays/ssr-overlay/ssr-overlay.component';
import { cleanMetricsContent } from '../../modules/analytics/util/ep-metrics-event.util';
import { EpStore } from '../store/ep-store';
import { AnalyticsActions, OverlayActions } from '../../actions';
import { fromEvent, Observable, from, merge } from 'rxjs';
import { filter, map, mergeMap, tap } from 'rxjs/operators';
import { OverlayMetadata } from '../../models/metadata.model';
import { SSRTextPlacementContentType } from '../../util/constructor-lookup/placement-constructor-lookup.util';
import { isSsrOverlay } from '../../components/overlays/overlay-predicates';

@singleton()
@autoInjectable()
export class OverlayService {

  static parseOverlayMetadata(domParent: HTMLElement, domTarget?: HTMLElement) {
    const parentTarget: any = domParent.querySelector<HTMLElement>('[data-overlay-metadata]') || {};
    const targetDataset = domTarget ? domTarget.dataset : {};

    const dataset = {...parentTarget.dataset, ...targetDataset};
    return new OverlayMetadata(dataset);
  }

  childComponent: SsrOverlayComponent | BaseOverlayComponent;
  overlayDismissActions$: Observable<UIEvent>;

  constructor(private logger: EpLogger,
              private analytics?: Analytics,
              private store?: EpStore) {
  }

  /*
  * Find the overlay div and create it if it
  * doesn't already exist
  * */
  findOrCreateOverlayDiv(): HTMLElement {
    const overlayId = 'epjs-overlay-div';
    let overlayEl = EpLocator.getElById(overlayId);
    if (!overlayEl) {
      overlayEl = EpLocator.createElement('div');
      overlayEl.id = overlayId;
      const body = EpLocator.getByTagName('body')[0];
      /*
      * TODO request animation frame
      * */
      body.appendChild(overlayEl);
    }
    return overlayEl;
  }

  getOverlayTarget(overlayDiv?: HTMLElement): HTMLElement {
    const targetId = 'bp-overlay-target';
    if (!overlayDiv) {
      overlayDiv = this.findOrCreateOverlayDiv();
    }

    if (!!overlayDiv) {
      return EpLocator.getElById(targetId, overlayDiv);
    }
  }

  /*
  * Take in show overlay action which has content id
  * Take optional brand presentment config and if not present,
  * then lookup from service later.
  * */
  async showOverlayContent(
    overlayContentId: string,
    originPlacementConfig?: PlacementConfig,
    brandPresentmentConfig?: GeneratePlacementResponse,
    originMetaData?: PlacementMetadata) {

    const content = getContentById(overlayContentId, brandPresentmentConfig);
    if (!content) {
      /*
      * TODO Handle content not found
      * */
      return;
    }

    await this.show(content, originPlacementConfig, originMetaData);
  }
  async setOverlayDisposeLock(lockOverlay: boolean, dismissTarget: any){
    if(this.childComponent && isSsrOverlay(this.childComponent)){
      this.childComponent.setOverlayDisposeLock(lockOverlay);
    }
  }

  getOverlayDisposeLock(overlay: SsrOverlayComponent | BaseOverlayComponent){
    if(overlay && isSsrOverlay(overlay)){
      return overlay.overlayDisposeLock;
    }else{
      return false;
    }
  }

  async show(placementContent: PlacementContentDef,
             originPlacementConfig?: PlacementConfig,
             originMetaData?: any,
             enableBackgroundDismiss = true) {

    const overlayConstructor = lookupOverlayConstructor(placementContent.contentType);
    const overlayBaseTemplate = getOverlayBaseTemplateByComponentType(placementContent.contentType);
    /*
    * Find the existing overlay div on the page or create a new
    * one.
    * */
    const overlayDiv = this.findOrCreateOverlayDiv();

    if (this.childComponent) {
      this.childComponent.dispose();
    }

    /*
    * Create the overlayComponent child
    * */
    this.childComponent = new overlayConstructor(placementContent as SSRTextPlacementContent, originPlacementConfig);
    await this.childComponent.init();

    /*
    * Just in case there are existing elements in
    * the overlay div, remove all child nodes before
    * adding the new one.
    * */
    this.removeChildNodes();

    overlayDiv.innerHTML = overlayBaseTemplate();

    const overlayTarget = this.getOverlayTarget();
    overlayTarget.appendChild(this.childComponent.getDomElement());
    this.childComponent.addedToDom();
    /*
    * Bind click events to specific elements in the
    * rendered template and overlay div to close the
    * modal and track click events.
    * */
    this.bindClickEvents(overlayDiv, enableBackgroundDismiss);

    const overlayMetadata = OverlayService.parseOverlayMetadata(
      this.childComponent.containerEl
    );

    /*
    * At this point the overlay should have been
    * shown. Trigger an analyticsClient event to
    * track the overlay impression.
    * */
    this.triggerViewOverlayEvent(placementContent,
      originPlacementConfig,
      originMetaData,
      overlayMetadata
    );
  }

  /*
  * View overlay event
  * */
  triggerViewOverlayEvent(placementContent: PlacementContentDef,
                          originPlacementConfig?: PlacementConfig,
                          originMetaData?: PlacementMetadata,
                          overlayMetadata?: OverlayMetadata) {

    /*
    * Dispatch event to public event emitter
    * */
    const eventData: BpMetricsViewOverlayProperties = {
      eventProperties: {
        overlayContent: cleanMetricsContent(placementContent),
        metadata: overlayMetadata
      },
      userProperties: {}
    };

    if(originPlacementConfig){
      eventData.eventProperties.originPlacement = this.analytics.getPlacementTrackingObj(originPlacementConfig);
      eventData.eventProperties.originPlacementContent = cleanMetricsContent(originPlacementConfig.content);
    }

    this.store.dispatch(AnalyticsActions.trackViewOverlayEvent({ eventData }));
  }

  triggerClickOverlayEvent(targetEl: HTMLElement,
                           placementContent: PlacementContentDef,
                           originPlacementConfig?: PlacementConfig,
                           originMetadata?: PlacementMetadata,
                           metadata?: OverlayMetadata) {

    /*
    * Get click target from data attribute
    * */
    const actionTarget = targetEl.dataset.actionTarget;

    /*
     * Build event properties object
     * */
    const eventData: BpMetricsClickOverlayProperties = {
      eventProperties: {
        overlayContent: cleanMetricsContent(placementContent),
        originMetadata,
        metadata,
        actionTarget
      },
      userProperties: {}
    };

    if(originPlacementConfig){
      eventData.eventProperties.originPlacement = this.analytics.getPlacementTrackingObj(originPlacementConfig);
      eventData.eventProperties.originPlacementContent = cleanMetricsContent(originPlacementConfig.content);
    }
    /*
     * Send overlayClickEvent
     * */
    this.analytics.v1ClickOverlayPost(eventData);
  }
  promptAndCloseOverlay(overlayComponent: SsrOverlayComponent | BaseOverlayComponent, dismissTarget?: HTMLDivElement){
    if(this.getOverlayDisposeLock(overlayComponent)){
      if(window.confirm('Changes may not be saved. Are you sure you want to exit?')){
        this.store.dispatch(OverlayActions.closeOverlay(dismissTarget));
      }
    }else{
      this.store.dispatch(OverlayActions.closeOverlay(dismissTarget));
    }
  }

  bindClickEvents(overlayDiv: HTMLElement, enableBackgroundDismiss = true) {

    const overlayDismissTargets = overlayDiv.querySelectorAll(
      '[data-epjs-overlay-dismiss]'
    );
    const overlayClickTargets = overlayDiv.querySelectorAll(
      '[data-epjs-overlay-click]'
    );

    /*
    * Only subscribe to events if there are more than
    * 0 nodes
    * */
    if (overlayDismissTargets.length > 0 && enableBackgroundDismiss) {
      (overlayDismissTargets[0] as HTMLElement).focus();
      const clickEvents = fromEvent<MouseEvent>(overlayDismissTargets, 'click');
      const enterEvents = fromEvent<KeyboardEvent>(overlayDismissTargets, 'keyup').pipe(filter((event) => event.code === 'Enter'));
      this.overlayDismissActions$ = merge(clickEvents, enterEvents);

      /*
       * When
       * - user clicks overlay background
       * Then
       * - dismiss overlay
       * - dispatch click overlay event with background target
       * */
      this.overlayDismissActions$.pipe(
        map(e => e.currentTarget),
        tap((dismissTarget: HTMLDivElement) =>
          this.promptAndCloseOverlay(this.childComponent, dismissTarget)
        )
      ).subscribe();
    } else if (overlayClickTargets.length > 0) {
      (overlayClickTargets[0] as HTMLElement).focus();
    }

    Array.prototype.forEach.call(
      overlayClickTargets,
      (clickTarget: HTMLDivElement) => {
        const metadata = OverlayService.parseOverlayMetadata(
          this.childComponent.containerEl,
          clickTarget
        );
        clickTarget.addEventListener('click', () => {
          this.triggerClickOverlayEvent(clickTarget,
            this.childComponent.placementContent,
            this.childComponent.originPlacementConfig,
            this.childComponent.originMetaData,
            metadata
            );
        });
      }
    );
  }

  /*
   * Find the overlay and remove it from the dom.
   * */
  async dismissOverlay(dismissTarget?: HTMLDivElement) {
    const overlayDiv = this.findOrCreateOverlayDiv();
    if (this.childComponent) {
      this.childComponent.dispose();
    }
    overlayDiv.remove();
  }

  removeChildNodes() {
    const overlayDiv = this.findOrCreateOverlayDiv();
    while (overlayDiv.firstChild) {
      overlayDiv.removeChild(overlayDiv.firstChild);
    }
    return overlayDiv;
  }
}
